import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getMessages } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, MessageSquare, Bot, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AddMessageModal from '../components/modals/AddMessageModal';

export default function Inbox() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const data = await getMessages();
      setMessages(data);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'emergency': return 'text-red-600 bg-red-100';
      case 'urgent': return 'text-orange-600 bg-orange-100';
      case 'routine': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'maintenance': return 'text-orange-600 bg-orange-100';
      case 'rent': return 'text-green-600 bg-green-100';
      case 'complaint': return 'text-red-600 bg-red-100';
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  return (
    <Layout>
      <div data-testid="inbox-page" className="max-w-7xl mx-auto animate-fade-in">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Inbox</h1>
            <p className="text-gray-600">AI-powered message triage and management</p>
          </div>
          <Button data-testid="new-message-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4" />
            New Message
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : messages.length === 0 ? (
          <Card data-testid="empty-state">
            <CardContent className="flex flex-col items-center justify-center py-20">
              <MessageSquare className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg mb-4">No messages yet</p>
              <Button data-testid="send-first-message-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4" />
                Send First Message
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <Card
                key={message.id}
                data-testid={`message-card-${message.id}`}
                className={`card-hover ${!message.is_read ? 'border-l-4 border-l-blue-500' : ''}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {!message.is_read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                        <h3 className="font-semibold text-gray-900">{message.subject}</h3>
                        {message.ai_categorized && (
                          <Bot className="w-4 h-4 text-purple-500" title="AI Processed" />
                        )}
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{message.content}</p>
                      
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`badge ${getCategoryColor(message.message_type)}`}>
                          {message.message_type}
                        </span>
                        
                        {message.ai_urgency && (
                          <span className={`badge ${getUrgencyColor(message.ai_urgency)} flex items-center gap-1`}>
                            <AlertCircle className="w-3 h-3" />
                            {message.ai_urgency}
                          </span>
                        )}
                        
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {format(new Date(message.created_at), 'MMM dd, yyyy HH:mm')}
                        </span>
                      </div>
                      
                      {message.ai_summary && (
                        <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-center gap-2 mb-1">
                            <Bot className="w-4 h-4 text-purple-600" />
                            <span className="text-xs font-semibold text-purple-900">AI Summary</span>
                          </div>
                          <p className="text-sm text-purple-900">{message.ai_summary}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <AddMessageModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={loadMessages}
        />
      </div>
    </Layout>
  );
}